import {
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Typography,
    IconButton,
    Tooltip,
    Button,
    Box,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import { parentUrl } from '../../../../utils/constants';
import { dateFormatter } from '../../../../utils/timeFormat';
import WidgetLoading from '../../Shared/WidgetLoading';
import { useAppDispatch } from '../../../../store/store';
import { generateRandomId, isDefaultDate } from '../../../../utils/common';
import { exportMembersNotScheduledWithBHOrNP, fetchMembersNotScheduledWithBHOrNP } from '../../../Widgets/WidgetService';
import { AppointmentTimeFilterEnum } from '../../../../Enum/AppointmentTimeFilterEnum';
import { useWidgetContext } from '../../../../hooks/widgetContext';
import { useNavigate } from 'react-router';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import b64toBlob from 'b64-to-blob';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchTeams } from '../../../../store/careTeams.slice';
import { DataGrid, gridClasses, GridColDef } from '@mui/x-data-grid';

const MembersNotScheduledWithBHorNP = (props) => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const timePeriods = [
        { label: AppointmentTimeFilterEnum.Next30DaysDescription, value: AppointmentTimeFilterEnum.Next30Days },
        { label: AppointmentTimeFilterEnum.Next60DaysDescription, value: AppointmentTimeFilterEnum.Next60Days },
        { label: AppointmentTimeFilterEnum.Next90DaysDescription, value: AppointmentTimeFilterEnum.Next90Days },
        { label: AppointmentTimeFilterEnum.CustomDescription, value: AppointmentTimeFilterEnum.Custom },
    ];
    const ref = useRef(null);
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const {
        scheduledFilterBy,
        setScheduledFilterBy,
        scheduledSelectedTeam,
        setScheduledSelectedTeam,
        scheduledShowInActiveTeams,
        setScheduledShowInActiveTeams,
        scheduledFromDate,
        setScheduledFromDate,
        scheduledToDate,
        setScheduledToDate,
    } = useWidgetContext();

    const [membersNotScheduled, setMembersNotScheduled] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);

    const initialPage = useCallback(async () => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    const handleClick = (event) => {
        setShow(!show);
    };

    useEffect(() => {
        const changeFilter = async () => {
            try {
                setIsLoading(true);
                setMembersNotScheduled([]);
                let dateFrom = Boolean(scheduledFromDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledFromDate).toISOString() : '';
                let dateTo = Boolean(scheduledToDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledToDate).toISOString() : '';
                const response = await fetchMembersNotScheduledWithBHOrNP(
                    scheduledSelectedTeam,
                    scheduledFilterBy,
                    dateFrom,
                    dateTo,
                    scheduledShowInActiveTeams,
                );

                setMembersNotScheduled(response);
                setIsLoading(false);
            } catch (e) {
                setMembersNotScheduled([]);
                setIsLoading(false);
                console.log(e);
            }
        };

        if (scheduledFilterBy !== AppointmentTimeFilterEnum.Custom || (Boolean(scheduledFromDate) && Boolean(scheduledToDate))) {
            changeFilter();
        }

        if (scheduledFilterBy !== AppointmentTimeFilterEnum.Custom && Boolean(scheduledFromDate) && Boolean(scheduledToDate)) {
            setScheduledFromDate(null);
            setScheduledToDate(null);
        }
    }, [scheduledFilterBy, scheduledSelectedTeam, scheduledShowInActiveTeams, scheduledFromDate, scheduledToDate]);

    const navigateToMembers = () => {
        history(`/provider/metricsummary/membersnotscheduledwithbhornpdetail`);
    };

    const exportDetail = async () => {
        setDownLoading(true);
        let dateFrom = Boolean(scheduledFromDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledFromDate).toISOString() : '';
        let dateTo = Boolean(scheduledToDate) && scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? new Date(scheduledToDate).toISOString() : '';
        const data = await exportMembersNotScheduledWithBHOrNP(
            scheduledSelectedTeam,
            scheduledFilterBy,
            dateFrom,
            dateTo,
            scheduledShowInActiveTeams,
        );
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `MemberNoScheduledWithBHorNP - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    const columns: GridColDef[] = [
        {
            field: 'Index',
            headerName: '#',
            width: 60,
            sortable: false,
            renderCell: (params) => {
                return <Typography>{params.api.getSortedRowIds().indexOf(params.id) + 1}</Typography>;
            },
            minWidth: 50,
            flex: 1,
        },
        {
            field: 'LastName',
            headerName: 'Name',
            minWidth: 180,
            flex: 1,
            renderCell: (params) => (
                <>
                    {params.row.LastName} {params.row.FirstName}
                </>
            ),
        },
        {
            field: 'DOB',
            headerName: 'DOB',
            minWidth: 120,
            flex: 1,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DOB) ? '-' : dateFormatter(new Date(params.row.DOB))}</>;
            },
        },
        {
            field: 'DateOfOnboard',
            headerName: 'Date Of Onboard',
            minWidth: 140,
            flex: 1.2,
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.DateOfOnboard) ? '-' : dateFormatter(new Date(params.row.DateOfOnboard))}</>;
            },
        },
        {
            field: 'LastNPVisit',
            minWidth: 130,
            flex: 1.2,
            renderHeader: () => (
                <Tooltip title="Last In-Person Visit With Licensed Provider" arrow>
                    <span>LIP Visit With LP</span>
                </Tooltip>
            ),
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastNPVisit) ? '-' : dateFormatter(new Date(params.row.LastNPVisit))}</>;
            },
        },
        {
            field: 'LastVisit',
            minWidth: 130,
            flex: 1.2,
            renderHeader: () => (
                <Tooltip title="Last In-Person Visit With Any Provider" arrow>
                    <span>LIP Visit With AP</span>
                </Tooltip>
            ),
            renderCell: (params) => {
                return <>{isDefaultDate(params.row.LastVisit) ? '-' : dateFormatter(new Date(params.row.LastVisit))}</>;
            },
        },
        {
            field: 'PrimaryProvider',
            headerName: 'Primary Provider',
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'IsContacted',
            flex: 1.2,
            minWidth: 110,
            renderHeader: () => (
                <Tooltip title="Contacted In Last Engagement" arrow>
                    <span>Contacted</span>
                </Tooltip>
            ),
            renderCell: (params) => {
                return <>{params.row.IsContacted ? 'Yes' : 'No'}</>;
            },
        },
    ];

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Members Not Scheduled With BH or NP
                                </span>
                            </Typography>
                            <Typography display={'inline-block'} variant="body1" color={'primary'} ref={ref}>
                                &#40;{membersNotScheduled.length}&#41;
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="caption">
                                                Use this widget to review active, uncompensated care, lost insurance and preparing for graduation members who do not have an appointment scheduled with a licensed provider in the upcoming date range selected.
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        {props.hasElevatedFilterAccess && (
                            <Grid size={2.5}>
                                <TeamDropdown
                                    teams={teams}
                                    selectedTeam={scheduledSelectedTeam}
                                    onChange={(id) => setScheduledSelectedTeam(id)}
                                    showInactive={scheduledShowInActiveTeams}
                                    onToggleInactive={setScheduledShowInActiveTeams}
                                />
                            </Grid>
                        )}
                        <Grid size={2.5}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={scheduledFilterBy}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setScheduledFilterBy(parseInt(e.target.value as any))}
                                >
                                    {timePeriods.map((date) => (
                                        <MenuItem key={date.value} value={date.value}>
                                            {date.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {scheduledFilterBy === AppointmentTimeFilterEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 2.5 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(scheduledFromDate) ? scheduledFromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => setScheduledFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 2.5 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(scheduledToDate) ? scheduledToDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => setScheduledToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                                    />
                                </Grid>
                                <Grid size={2} display={'flex'} mb={2} maxHeight="55px">
                                    <Button
                                        style={{ minWidth: '105px', marginLeft: 'auto' }}
                                        onClick={() => exportDetail()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading || membersNotScheduled?.length === 0}
                                    >
                                        {downLoading ? 'Exporting...' : 'Export'}
                                    </Button>
                                </Grid>
                            </React.Fragment>
                        ) : (
                            <>
                                <Grid size={{ md: 5 }}></Grid>
                                <Grid size={2} display={'flex'} mb={2} maxHeight="55px">
                                    <Button
                                        style={{ minWidth: '105px', marginLeft: 'auto' }}
                                        onClick={() => exportDetail()}
                                        variant="contained"
                                        color="success"
                                        disabled={downLoading || !Boolean(membersNotScheduled?.length)}
                                    >
                                        {downLoading ? 'Exporting...' : 'Export'}
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <Box display="flex" position="relative" height='calc(100vh - 285px)'>
                        {
                            isLoading ? <WidgetLoading />
                                : membersNotScheduled.length > 0 ?
                                    <Grid size={12} style={{ maxHeight: '500px', height: '75vh', width: '100%' }}>
                                        <DataGrid
                                            columns={columns}
                                            rows={membersNotScheduled}
                                            getRowId={(row) => `${row.Id}-${generateRandomId()}`}
                                            paginationMode="server"
                                            hideFooterPagination={true}
                                            hideFooter={true}
                                            onCellClick={(params: any) => {
                                                window.open(`${parentUrl}provider/members-list/member-chart/${params.row.Id}/member-profile`, '_blank');
                                            }}
                                            sx={{
                                                [`& .${gridClasses.cell}`]: {
                                                    py: 2,
                                                },
                                            }}
                                        />
                                    </Grid>
                                    : <Box display="flex" width="100%" justifyContent="center" alignItems="center">
                                        <Typography variant="h6">No Matching Records</Typography>
                                    </Box>
                        }
                    </Box>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default MembersNotScheduledWithBHorNP;
